import { Box, ButtonGroup, Button, Typography, useTheme, LinearProgress, Tooltip, Switch, FormControlLabel, Card, CardContent, Chip, Skeleton, Dialog, DialogContent, DialogTitle, IconButton } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { lightenHexColor, generatePDF, calculatePercentageChange } from '../../../dwi-functions';
import { useState, useContext, useEffect, useRef, useMemo } from "react";
import { AppVarsContext } from '../../../context/AppVarsContext';
import { FcPrint } from 'react-icons/fc';
import CreativeDetailTabs from '../CreativeDetailsTab';
import ChartCard from '../../../report-components/stackadapt-chart-card';
import TrendChart from '../../../report-components/trend-chart';
import moment from 'moment';
import { useToken } from '../../../Login/TokenProvider';
import TableCard from '../../../system/table-card';
import ViMarcTableCard from '../../../system/vimarc-table-card';
import { NumericFormat } from 'react-number-format';
import CloseIcon from '@mui/icons-material/Close';
import { getReportSurfaceSx } from '../../shared/report-design';
import StackAdaptSpendStrip from '../channel-spend-strip';
import { getChannelTrendData } from '../trend-data';
import { buildChannelMetricBundle } from '../channel-metrics';
import { buildChannelCompletionBundle, getCompletionConfig, getProgrammaticCompletionMetrics } from '../completion-metrics';
import { resolveShowCost } from '../../../system/report-visibility';
import { formatUsd } from '../../../system/report-formatters';



const safeNum = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
};

const getMetricDelta = (metric, previousMetric) => {
    const explicitDelta = Number(metric?.delta);
    if (metric?.delta != null && Number.isFinite(explicitDelta)) return explicitDelta;
    return calculatePercentageChange(safeNum(metric?.count), safeNum(previousMetric?.count));
};

const formatNumber = (value) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(safeNum(value));
const formatPercent = (value) => `${safeNum(value).toFixed(2)}%`;
const formatCurrency = (value) => formatUsd(safeNum(value));

const isLikelyUrl = (value) => {
    if (!value) return false;
    const text = String(value).trim();
    return /^https?:\/\//i.test(text);
};

const collectUrlStrings = (input, urls = []) => {
    if (input == null) return urls;
    if (typeof input === 'string') {
        const text = input.trim();
        if (isLikelyUrl(text)) {
            urls.push(text);
            return urls;
        }
        const matches = text.match(/https?:\/\/[^\s"'<>]+/g);
        if (matches) {
            matches.forEach((match) => urls.push(match));
        }
        return urls;
    }
    if (Array.isArray(input)) {
        input.forEach((item) => collectUrlStrings(item, urls));
        return urls;
    }
    if (typeof input === 'object') {
        Object.values(input).forEach((val) => collectUrlStrings(val, urls));
        return urls;
    }
    return urls;
};

const normalizeAssetUrls = (source) => {
    const output = [];
    const push = (val) => {
        if (val == null) return;
        if (Array.isArray(val)) {
            val.forEach((item) => push(item));
            return;
        }
        if (typeof val === 'object') {
            const url =
                val.IMAGE_ASSET_URL ||
                val.IMAGE_URL ||
                val.THUMBNAIL_URL ||
                val.thumbnail_url ||
                val.thumbnail ||
                val.ASSET_URL ||
                val.asset_url ||
                val.PREVIEW_URL ||
                val.preview_url ||
                val.CREATIVE_URL ||
                val.MEDIA_URL ||
                val.AUDIO_URL ||
                val.VIDEO_URL ||
                val.FILE_URL ||
                val.URL ||
                val.url ||
                val.image_url ||
                val.image ||
                val.name;
            if (isLikelyUrl(url)) output.push(String(url));
            if (isLikelyUrl(val.name)) output.push(String(val.name));
            collectUrlStrings(val).forEach((urlItem) => output.push(urlItem));
            return;
        }
        if (isLikelyUrl(val)) output.push(String(val));
        collectUrlStrings(val).forEach((urlItem) => output.push(urlItem));
    };
    push(source);
    return Array.from(new Set(output.filter(Boolean)));
};

const pickPreferredUrl = (urls, extensions) => {
    if (!urls.length) return '';
    if (!extensions || !extensions.length) return urls[0];
    const lowered = extensions.map((ext) => ext.toLowerCase());
    const matched = urls.find((item) => {
        const full = String(item).toLowerCase();
        const cleaned = full.split('?')[0];
        return lowered.some((ext) => cleaned.endsWith(ext) || full.includes(ext));
    });
    return matched || urls[0];
};

const getAssetPreviewUrl = (row, preferredType) => {
    const urls = normalizeAssetUrls([
        row?.IMAGE_ASSET_URL,
        row?.IMAGE_URL,
        row?.IMAGE_URLS,
        row?.ASSET_URL,
        row?.ASSET_URLS,
        row?.CREATIVE_URL,
        row?.MEDIA_URL,
        row?.AUDIO_URL,
        row?.VIDEO_URL,
        row?.FILE_URL,
        row?.THUMBNAIL_URL,
        row?.thumbnail,
        row?.PREVIEW_URL,
        row?.URL,
        row?.url,
        row?.ASSETS,
        row?.ASSET,
        row?.creative,
        row?.CREATIVE,
        row
    ]);
    const audioExtensions = ['.mp3', '.wav', '.m4a', '.aac', '.ogg', '.flac'];
    const videoExtensions = ['.mp4', '.mov', '.webm', '.m4v', '.mkv', '.avi'];
    if (preferredType === 'audio') {
        return pickPreferredUrl(urls, audioExtensions);
    }
    if (preferredType === 'video') {
        return pickPreferredUrl(urls, videoExtensions);
    }
    return pickPreferredUrl(urls, []);
};

const ChannelLoadingSkeleton = ({ theme }) => {
    const surfaceCardSx = getReportSurfaceSx(theme);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 1 }}>
            <Box sx={{ ...surfaceCardSx, p: 1.25 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {Array.from({ length: 8 }).map((_, index) => (
                        <Skeleton
                            key={`stackadapt-video-skel-${index}`}
                            variant="rectangular"
                            height={90}
                            sx={{ flex: '1 1 160px', borderRadius: 2 }}
                        />
                    ))}
                </Box>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ ...surfaceCardSx, flex: '1 1 320px', p: 1.25 }}>
                    <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 2 }} />
                </Box>
                <Box sx={{ ...surfaceCardSx, flex: '1 1 320px', p: 1.25 }}>
                    <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 2 }} />
                </Box>
            </Box>
            <Box sx={{ ...surfaceCardSx, p: 1.25 }}>
                <Skeleton variant="rectangular" height={240} sx={{ borderRadius: 2 }} />
            </Box>
        </Box>
    );
};





const Video = (props) => {
    const channelKey = props.channelKey || 'video';
    const channelTitle = props.title || (channelKey === 'ctv' ? 'CTV' : 'Video');
    const pDate = new Intl.DateTimeFormat('en-US', { month: 'long', day: '2-digit', year: 'numeric', timeZone: "UTC" }).format(new Date(sessionStorage.getItem('sDate'))) + "-" + new Intl.DateTimeFormat('en-US', { month: 'long', day: '2-digit', year: 'numeric', timeZone: "UTC" }).format(new Date(sessionStorage.getItem('eDate')));
    const theme = useTheme();
    const { appVars, appLoading } = useContext(AppVarsContext);
    const reportRef1 = useRef()
    const token = useToken();
    const admintoken = token.token.token;
    const [client] = useState(props.client);
    const [showTrend, setShowTrend] = useState(true);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxSrc, setLightboxSrc] = useState('');
    const [lightboxTitle, setLightboxTitle] = useState('');
    const isVideoSrc = (src) => /\.(mp4|mov|webm|m4v|mkv|avi)(\?|$)/i.test(String(src || ''));

    const openLightbox = (src, title) => {
        if (!src) return;
        setLightboxSrc(src);
        setLightboxTitle(title || 'Creative Preview');
        setLightboxOpen(true);
    };

    const closeLightbox = () => {
        setLightboxOpen(false);
        setLightboxSrc('');
        setLightboxTitle('');
    };
    const headerStyleObj = {
        textAlign: 'center',
        backgroundColor: lightenHexColor(theme.palette.primary.main, 0),
        color: lightenHexColor(theme.palette.primary.light, 100),
        border: '1px solid ' + theme.palette.secondary.main,
        paddingTop: 5,
        paddingBottom: 5,
        maxWidth: 75,
    };




    async function fetchData() {

    }



    useEffect(() => {


        if (!appLoading) {
            fetchData();

        }

        return () => {

        };
    }, [appLoading]);

    const trendData = getChannelTrendData(props.data, channelKey);
    const awarenessMode = Boolean(props.awarenessMode || props.retailMode || props.vimarcMode);
    const ReportTableCard = awarenessMode ? ViMarcTableCard : TableCard;
    const completionConfig = useMemo(() => getCompletionConfig(channelKey), [channelKey]);
    const showCost = resolveShowCost(props.reportProfile);
    const showRevenue = !awarenessMode;
    const surfaceCardSx = useMemo(() => getReportSurfaceSx(theme), [theme]);
    const statTileSx = useMemo(() => ({
        ...surfaceCardSx,
        display: 'flex',
        flex: 1,
        color: theme.palette.primary.main,
        gap: 1,
        p: 1.25,
        alignItems: 'center',
        justifyContent: 'space-between'
    }), [surfaceCardSx, theme]);
    const sectionCardSx = useMemo(() => ({
        ...surfaceCardSx,
        p: { xs: 1.25, sm: 1.5 }
    }), [surfaceCardSx]);
    const campaignRows = useMemo(() => {
        const rows = props?.campaignData?.[channelKey]?.campaigns || props?.data?.channel?.[channelKey]?.campaigns || [];
        return Array.isArray(rows) ? rows : [];
    }, [channelKey, props.campaignData, props.data]);
    const previousCampaignRows = useMemo(() => {
        const rows = props?.data?.previousChannel?.[channelKey]?.campaigns || [];
        return Array.isArray(rows) ? rows : [];
    }, [channelKey, props.data]);
    const channelMetrics = useMemo(() => buildChannelMetricBundle({
        channel: props?.data?.channel?.[channelKey],
        previousChannel: props?.data?.previousChannel?.[channelKey],
        campaignRows,
        previousCampaignRows
    }), [campaignRows, channelKey, previousCampaignRows, props.data]);
    const completionMetrics = useMemo(() => buildChannelCompletionBundle({
        channel: props?.data?.channel?.[channelKey],
        previousChannel: props?.data?.previousChannel?.[channelKey],
        campaignRows,
        previousCampaignRows,
        channelKey
    }), [campaignRows, channelKey, previousCampaignRows, props.data]);

    const buildMetrics = (row) => {
        const impressions = safeNum(row?.IMPRESSIONS ?? row?.impressions);
        const clicks = safeNum(row?.CLICKS ?? row?.clicks);
        const conversions = safeNum(row?.CONVERSIONS ?? row?.conversions);
        const cost = safeNum(row?.COST ?? row?.cost);
        const revenue = safeNum(row?.REVENUE ?? row?.revenue);
        const ctr = safeNum(row?.CTR ?? row?.ctr ?? (impressions ? (clicks / impressions) * 100 : 0));
        const convRate = safeNum(row?.CONV_RATE ?? row?.CONVRATE ?? row?.convrate ?? (clicks ? (conversions / clicks) * 100 : 0));
        const avgGift = safeNum(row?.AVG_GIFT ?? row?.avgGift ?? row?.avg_gift ?? (conversions ? revenue / conversions : 0));
        const roas = safeNum(row?.ROAS ?? row?.roas ?? (cost ? revenue / cost : 0));
        const completion = getProgrammaticCompletionMetrics(row, channelKey);
        return { impressions, clicks, conversions, cost, revenue, ctr, convRate, avgGift, roas, completedCount: completion.completedCount, completionRate: completion.completionRate };
    };

    const topGroupByCtr = useMemo(() => {
        const base = campaignRows.map((row) => ({ row, metrics: buildMetrics(row) }));
        const scored = base.filter((item) => item.metrics.impressions > 0 || item.metrics.clicks > 0);
        const ranked = scored.length ? scored : base;
        ranked.sort((a, b) => b.metrics.ctr - a.metrics.ctr);
        return ranked[0] || null;
    }, [campaignRows]);

    const topGroupByConvRate = useMemo(() => {
        const base = campaignRows.map((row) => ({ row, metrics: buildMetrics(row) }));
        const scored = base.filter((item) => item.metrics.clicks > 0);
        const ranked = scored.length ? scored : base;
        ranked.sort((a, b) => b.metrics.convRate - a.metrics.convRate);
        return ranked[0] || null;
    }, [campaignRows]);

    const topGroupByImpressions = useMemo(() => {
        const base = campaignRows.map((row) => ({ row, metrics: buildMetrics(row) }));
        const scored = base.filter((item) => item.metrics.impressions > 0);
        const ranked = scored.length ? scored : base;
        ranked.sort((a, b) => b.metrics.impressions - a.metrics.impressions);
        return ranked[0] || null;
    }, [campaignRows]);
    const topGroupByCompletion = useMemo(() => {
        const base = campaignRows.map((row) => ({ row, metrics: buildMetrics(row) }));
        const scored = base.filter((item) => item.metrics.completedCount > 0);
        if (!scored.length) return null;
        scored.sort((a, b) => b.metrics.completedCount - a.metrics.completedCount);
        return scored[0] || null;
    }, [campaignRows]);

    const pickIdValue = (...values) => (
        values.find((value) => (
            (typeof value === 'string' && value.trim() !== '') || typeof value === 'number'
        )) || null
    );

    const getGroupId = (row) => pickIdValue(
        row?.ad_group_id,
        row?.adgroup_id,
        row?.adGroupId,
        row?.adgroupId,
        row?.AD_GROUP_ID,
        row?.ADGROUP_ID,
        row?.GROUP_ID,
        row?.group_id,
        row?.groupId,
        row?.id,
        row?.ID,
        row?.campaign_id,
        row?.campaignId,
        row?.campaignID
    );

    const topGroupIds = useMemo(() => {
        const ids = [
            getGroupId(topGroupByCtr?.row),
            getGroupId((awarenessMode ? (topGroupByCompletion || topGroupByImpressions) : topGroupByConvRate)?.row)
        ].filter(Boolean);
        return Array.from(new Set(ids));
    }, [awarenessMode, topGroupByCompletion, topGroupByCtr, topGroupByConvRate, topGroupByImpressions]);

    const [creativeMap, setCreativeMap] = useState({});

    useEffect(() => {
        if (!appVars?.api_url) return;
        if (!topGroupIds.length) return;
        const missing = topGroupIds.filter((id) => !creativeMap[id]);
        if (!missing.length) return;
        let isActive = true;
        const fetchCreatives = async () => {
            const results = await Promise.all(
                missing.map(async (id) => {
                    try {
                        const raw = JSON.stringify({
                            campaign: id,
                            cid: sessionStorage.getItem('rid'),
                            sdate: sessionStorage.getItem('sDate'),
                            edate: sessionStorage.getItem('eDate')
                        });
                        const res = await fetch(`${appVars.api_url}/stackadapt/creatives/`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: raw,
                            redirect: 'follow'
                        });
                        const data = await res.json();
                        return [id, Array.isArray(data) ? data : []];
                    } catch (error) {
                        return [id, []];
                    }
                })
            );
            if (!isActive) return;
            setCreativeMap((prev) => {
                const next = { ...prev };
                results.forEach(([id, rows]) => {
                    next[id] = rows;
                });
                return next;
            });
        };
        fetchCreatives();
        return () => {
            isActive = false;
        };
    }, [topGroupIds, appVars?.api_url, creativeMap]);

    const getCreativeWinnerForGroup = (row, metricKey) => {
        const id = getGroupId(row);
        const creatives = id ? creativeMap[id] || [] : [];
        if (!creatives.length) return null;
        const scored = creatives
            .map((creative) => ({ creative, metrics: buildMetrics(creative) }))
            .filter((item) => item.metrics.impressions > 0 || item.metrics.clicks > 0);
        if (!scored.length) {
            return { creative: creatives[0], metrics: buildMetrics(creatives[0]) };
        }
        const key = metricKey === 'convRate' ? 'convRate' : (metricKey === 'impressions' ? 'impressions' : 'ctr');
        scored.sort((a, b) => b.metrics[key] - a.metrics[key]);
        return scored[0];
    };

    const topConvGroup = awarenessMode ? (topGroupByCompletion || topGroupByImpressions) : topGroupByConvRate;
    const topCtrWinner = useMemo(() => (
        topGroupByCtr ? getCreativeWinnerForGroup(topGroupByCtr.row, 'ctr') : null
    ), [topGroupByCtr, creativeMap]);

    const topConvWinner = useMemo(() => (
        topConvGroup ? getCreativeWinnerForGroup(topConvGroup.row, awarenessMode ? 'impressions' : 'convRate') : null
    ), [awarenessMode, topConvGroup, creativeMap]);

    const ctrGroupId = topGroupByCtr ? getGroupId(topGroupByCtr.row) : null;
    const convGroupId = topConvGroup ? getGroupId(topConvGroup.row) : null;
    const ctrCreatives = ctrGroupId ? creativeMap[ctrGroupId] : null;
    const convCreatives = convGroupId ? creativeMap[convGroupId] : null;

    const getAdLabel = (row) => {
        const label = row?.ad_group_name || row?.adgroup_name || row?.group_name || row?.GROUP_NAME || row?.name || row?.NAME || row?.campaign || row?.CAMPAIGN || row?.label || row?.AD_NAME;
        if (isLikelyUrl(label)) return 'Ad Group';
        return label || 'Ad Group';
    };
    const getCreativeLabel = (row) => {
        const label = row?.creative || row?.CREATIVE || row?.CREATIVE_NAME || row?.NAME || row?.name || row?.AD_NAME || row?.AD_ID || row?.id;
        if (isLikelyUrl(label)) return 'Creative';
        return label || 'Creative';
    };
    const tableColumns = useMemo(() => ([
        {
            header: "Campaign",
            field: "name",
            grow: true,
            minSize: 300,
            Cell: ({ cell }) => (
                <Typography textAlign={'right'} fontSize={13} fontWeight={900}>{cell.row.original.name}</Typography>
            ),
            Footer: () => <Typography fontSize={13} fontWeight={900} textAlign={'right'}>Totals</Typography>,
        },
        {
            header: completionConfig?.countLabel || 'Video Completed',
            accessorKey: 'completedCount',
            Cell: ({ cell }) => <NumericFormat decimalScale={0} thousandSeparator="," value={cell.row.original.completedCount || 0} displayType="text" />,
            Footer: () => <NumericFormat decimalScale={0} thousandSeparator="," value={completionMetrics.currentCompleted.count} displayType="text" />
        },
        {
            header: completionConfig?.rateLabel || 'Video Completion Rate',
            accessorKey: 'completionRate',
            Cell: ({ cell }) => <NumericFormat decimalScale={2} value={cell.row.original.completionRate || 0} displayType="text" suffix="%" />,
            Footer: () => <NumericFormat decimalScale={2} value={completionMetrics.currentRate.count} displayType="text" suffix="%" />
        }
    ]), [completionConfig?.countLabel, completionConfig?.rateLabel, completionMetrics.currentCompleted.count, completionMetrics.currentRate.count]);


    return (
        <>
            {props.loading ? <ChannelLoadingSkeleton theme={theme} /> : (
            (props?.data?.channel?.[channelKey] ?
                <>
                <StackAdaptSpendStrip
                    showCost={showCost}
                    showRevenue={showRevenue}
                    title={channelTitle}
                    cost={channelMetrics.currentStats.cost.count}
                    revenue={channelMetrics.currentStats.revenue.count}
                    tileSx={statTileSx}
                />

                <Box ref={reportRef1} style={{ padding: theme.spacing(0) }}>



                    {/* Impressions */}
                    <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' }, mt: 1 }} >

                        <ChartCard reportStyle
                            title="Impressions"
                            explain={JSON.parse(appVars.tooltips).impression}
                            data={channelMetrics.currentStats.impressions}
                            previousdata={channelMetrics.previousStats.impressions}
                            delta={getMetricDelta(channelMetrics.currentStats.impressions, channelMetrics.previousStats.impressions)}
                            themeColor={{
                                primary: theme.palette.primary.main,
                                secondary: theme.palette.primary.main
                            }}
                            view="stackadapt"
hideChart={true}
                        />

                        <ChartCard reportStyle
                            title="Clicks"
                            explain={JSON.parse(appVars.tooltips).click}
                            data={channelMetrics.currentStats.clicks}
                            previousdata={channelMetrics.previousStats.clicks}
                            delta={getMetricDelta(channelMetrics.currentStats.clicks, channelMetrics.previousStats.clicks)}
                            themeColor={{
                                primary: theme.palette.primary.main,
                                secondary: theme.palette.primary.main
                            }}

                            view="stackadapt"
hideChart={true}
                        />

                        <ChartCard reportStyle
                            title="CTR"
                            explain={JSON.parse(appVars.tooltips).ctr}
                            data={channelMetrics.currentStats.ctr}
                            previousdata={channelMetrics.previousStats.ctr}
                            delta={getMetricDelta(channelMetrics.currentStats.ctr, channelMetrics.previousStats.ctr)}
                            themeColor={{
                                primary: theme.palette.primary.main,
                                secondary: theme.palette.primary.main
                            }}

                            view="stackadapt"
hideChart={true}
                        />

                        <ChartCard reportStyle
                            title={completionConfig?.countLabel || 'Video Completed'}
                            explain="Completed video plays based on StackAdapt completion milestones."
                            data={completionMetrics.currentCompleted}
                            previousdata={completionMetrics.previousCompleted}
                            delta={getMetricDelta(completionMetrics.currentCompleted, completionMetrics.previousCompleted)}
                            themeColor={{
                                primary: theme.palette.primary.main,
                                secondary: theme.palette.primary.main
                            }}

                            view="stackadapt"
hideChart={true}
                        />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' }, mt: 1 }} >
                        <ChartCard reportStyle
                            title="Conversions"
                            explain={JSON.parse(appVars.tooltips).conversion}
                            data={channelMetrics.currentStats.conversions}
                            previousdata={channelMetrics.previousStats.conversions}
                            delta={getMetricDelta(channelMetrics.currentStats.conversions, channelMetrics.previousStats.conversions)}
                            themeColor={{
                                primary: theme.palette.primary.main,
                                secondary: theme.palette.primary.main
                            }}

                            view="stackadapt"
hideChart={true}
                        />

                        {showRevenue && (
                            <ChartCard reportStyle
                                title="Revenue"
                                explain={JSON.parse(appVars.tooltips).revenue}
                                data={channelMetrics.currentStats.revenue}
                                previousdata={channelMetrics.previousStats.revenue}
                                delta={getMetricDelta(channelMetrics.currentStats.revenue, channelMetrics.previousStats.revenue)}
                                themeColor={{
                                    primary: theme.palette.primary.main,
                                    secondary: theme.palette.primary.main
                                }}

                                view="stackadapt"
hideChart={true}
                            />
                        )}

                        <ChartCard reportStyle
                            title={completionConfig?.rateLabel || 'Video Completion Rate'}
                            explain="Share of video delivery that reached the completion threshold."
                            data={completionMetrics.currentRate}
                            previousdata={completionMetrics.previousRate}
                            delta={getMetricDelta(completionMetrics.currentRate, completionMetrics.previousRate)}
                            themeColor={{
                                primary: theme.palette.primary.main,
                                secondary: theme.palette.primary.main
                            }}

                            view="stackadapt"
hideChart={true}
                        />

                        <ChartCard reportStyle
                            title="Conversion Rate"
                            explain={JSON.parse(appVars.tooltips).conv_rate}
                            data={channelMetrics.currentStats.convrate}
                            previousdata={channelMetrics.previousStats.convrate}
                            delta={getMetricDelta(channelMetrics.currentStats.convrate, channelMetrics.previousStats.convrate)}
                            themeColor={{
                                primary: theme.palette.primary.main,
                                secondary: theme.palette.primary.main
                            }}

                            view="stackadapt"
hideChart={true}
                        />

                        {showRevenue && (
                            <ChartCard reportStyle
                                title="Avg Transaction"
                                explain={JSON.parse(appVars.tooltips).avg_gift}
                                data={channelMetrics.currentStats.avgGift}
                                previousdata={channelMetrics.previousStats.avgGift}
                                delta={getMetricDelta(channelMetrics.currentStats.avgGift, channelMetrics.previousStats.avgGift)}
                                themeColor={{
                                    primary: theme.palette.primary.main,
                                    secondary: theme.palette.primary.main
                                }}

                                view="stackadapt"
hideChart={true}
                            />
                        )}

                        {showCost && (
                            <ChartCard reportStyle
                                title="ROAS"
                                explain={JSON.parse(appVars.tooltips).roas || JSON.parse(appVars.tooltips).roi || ''}
                                data={channelMetrics.currentStats.roas}
                                previousdata={channelMetrics.previousStats.roas}
                                delta={getMetricDelta(channelMetrics.currentStats.roas, channelMetrics.previousStats.roas)}
                                themeColor={{
                                    primary: theme.palette.primary.main,
                                    secondary: theme.palette.primary.main
                                }}

                                view="stackadapt"
hideChart={true}
                            />
                        )}

                    </Box>

                    {(topGroupByCtr || topConvGroup) && (
                        <Box sx={{ mt: 1 }}>
              
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                <Box sx={{ flexGrow: 1, flexBasis: { xs: '100%', lg: 'calc(50% - 16px)' } }}>
                                    <Card variant="outlined" sx={surfaceCardSx}>
                                        <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                Top Ads by CTR
                                            </Typography>
                                            {topCtrWinner ? (
                                                <Box
                                                    sx={{
                                                        border: '1px solid',
                                                        borderColor: 'divider',
                                                        borderRadius: 1,
                                                        p: 1.5,
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: 1
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            gap: 1,
                                                            flexDirection: { xs: 'column', sm: 'row' },
                                                            alignItems: 'stretch'
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                border: '1px solid',
                                                                borderColor: 'divider',
                                                                borderRadius: 1,
                                                                overflow: 'hidden',
                                                                minHeight: 100,
                                                                background: '#fff',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                flex: { xs: '0 0 auto', sm: '0 0 140px' },
                                                                width: { xs: '100%', sm: 140 },
                                                                cursor: getAssetPreviewUrl(topCtrWinner.creative, 'video') ? 'zoom-in' : 'default'
                                                            }}
                                                            onClick={() => openLightbox(getAssetPreviewUrl(topCtrWinner.creative, 'video'), getCreativeLabel(topCtrWinner.creative))}
                                                        >
                                                            {getAssetPreviewUrl(topCtrWinner.creative, 'video') ? (
                                                                <Box
                                                                    component="video"
                                                                    src={getAssetPreviewUrl(topCtrWinner.creative, 'video')}
                                                                    controls
                                                                    preload="metadata"
                                                                    sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                                                />
                                                            ) : (
                                                                <Typography variant="caption" color="text.secondary">
                                                                    No video preview
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1 }}>
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    CTR {formatPercent(topCtrWinner.metrics.ctr)}
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {formatNumber(topCtrWinner.metrics.clicks)} clicks
                                                                </Typography>
                                                            </Box>
                                                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                                {getCreativeLabel(topCtrWinner.creative)}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                Group: {getAdLabel(topGroupByCtr?.row)} - {props.title}
                                                            </Typography>
                                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                                                <Chip size="small" label={`Impr ${formatNumber(topCtrWinner.metrics.impressions)}`} />
                                                                <Chip size="small" label={`Conv ${formatNumber(topCtrWinner.metrics.conversions)}`} />
                                                                <Chip size="small" label={`Complete ${formatNumber(topCtrWinner.metrics.completedCount)}`} />
                                                                <Chip size="small" label={`Rate ${formatPercent(topCtrWinner.metrics.completionRate)}`} />
                                                                {showCost ? <Chip size="small" label={`Spend ${formatCurrency(topCtrWinner.metrics.cost)}`} /> : null}
                                                                {showRevenue ? <Chip size="small" label={`Revenue ${formatCurrency(topCtrWinner.metrics.revenue)}`} /> : null}
                                                                {showCost ? <Chip size="small" label={`ROAS ${topCtrWinner.metrics.roas.toFixed(2)}x`} /> : null}
                                                            </Box>
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">
                                                    {topGroupByCtr
                                                        ? (ctrCreatives === undefined ? 'Loading creative details...' : 'No creative data available.')
                                                        : 'No CTR data available.'}
                                                </Typography>
                                            )}
                                        </CardContent>
                                    </Card>
                                </Box>
                                <Box sx={{ flexGrow: 1, flexBasis: { xs: '100%', lg: 'calc(50% - 16px)' } }}>
                                    <Card variant="outlined" sx={surfaceCardSx}>
                                        <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                {awarenessMode
                                                    ? ((topGroupByCompletion && completionMetrics.currentCompleted.count > 0)
                                                        ? `Top Ads by ${completionConfig?.countLabel || 'Video Completed'}`
                                                        : 'Top Ads by Impressions')
                                                    : 'Top Ads by Conversion Rate'}
                                            </Typography>
                                            {topConvWinner ? (
                                                <Box
                                                    sx={{
                                                        border: '1px solid',
                                                        borderColor: 'divider',
                                                        borderRadius: 1,
                                                        p: 1.5,
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: 1
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            gap: 1,
                                                            flexDirection: { xs: 'column', sm: 'row' },
                                                            alignItems: 'stretch'
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                border: '1px solid',
                                                                borderColor: 'divider',
                                                                borderRadius: 1,
                                                                overflow: 'hidden',
                                                                minHeight: 100,
                                                                background: '#fff',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                flex: { xs: '0 0 auto', sm: '0 0 140px' },
                                                                width: { xs: '100%', sm: 140 },
                                                                cursor: getAssetPreviewUrl(topConvWinner.creative, 'video') ? 'zoom-in' : 'default'
                                                            }}
                                                            onClick={() => openLightbox(getAssetPreviewUrl(topConvWinner.creative, 'video'), getCreativeLabel(topConvWinner.creative))}
                                                        >
                                                            {getAssetPreviewUrl(topConvWinner.creative, 'video') ? (
                                                                <Box
                                                                    component="video"
                                                                    src={getAssetPreviewUrl(topConvWinner.creative, 'video')}
                                                                    controls
                                                                    preload="metadata"
                                                                    sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                                                />
                                                            ) : (
                                                                <Typography variant="caption" color="text.secondary">
                                                                    No video preview
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1 }}>
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {awarenessMode
                                                                        ? ((topGroupByCompletion && topConvWinner.metrics.completedCount > 0)
                                                                            ? `${completionConfig?.countLabel || 'Video Completed'} ${formatNumber(topConvWinner.metrics.completedCount)}`
                                                                            : `Impr ${formatNumber(topConvWinner.metrics.impressions)}`)
                                                                        : `Conv ${formatPercent(topConvWinner.metrics.convRate)}`}
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {awarenessMode ? `${formatNumber(topConvWinner.metrics.clicks)} clicks` : `${formatNumber(topConvWinner.metrics.conversions)} conv`}
                                                                </Typography>
                                                            </Box>
                                                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                                {getCreativeLabel(topConvWinner.creative)}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                Group: {getAdLabel(topConvGroup?.row)} - {props.title}
                                                            </Typography>
                                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                                                <Chip size="small" label={`Clicks ${formatNumber(topConvWinner.metrics.clicks)}`} />
                                                                <Chip size="small" label={`Impr ${formatNumber(topConvWinner.metrics.impressions)}`} />
                                                                <Chip size="small" label={`Complete ${formatNumber(topConvWinner.metrics.completedCount)}`} />
                                                                <Chip size="small" label={`Rate ${formatPercent(topConvWinner.metrics.completionRate)}`} />
                                                                {showRevenue ? (
                                                                    <>
                                                                        <Chip size="small" label={`Revenue ${formatCurrency(topConvWinner.metrics.revenue)}`} />
                                                                        <Chip size="small" label={`Avg ${formatCurrency(topConvWinner.metrics.avgGift)}`} />
                                                                    </>
                                                                ) : null}
                                                                {showCost ? <Chip size="small" label={`ROAS ${topConvWinner.metrics.roas.toFixed(2)}x`} /> : null}
                                                            </Box>
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">
                                                    {topGroupByConvRate
                                                        ? (convCreatives === undefined ? 'Loading creative details...' : 'No creative data available.')
                                                        : (awarenessMode ? 'No impression data available.' : 'No conversion rate data available.')}
                                                </Typography>
                                            )}
                                        </CardContent>
                                    </Card>
                                </Box>
                            </Box>
                        </Box>
                    )}

                    {trendData && Array.isArray(trendData) && trendData.length > 0 && (
                        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                            <FormControlLabel
                                control={<Switch size="small" checked={showTrend} onChange={(event) => setShowTrend(event.target.checked)} />}
                                label="Trend"
                            />
                        </Box>
                    )}

                    {trendData && Array.isArray(trendData) && trendData.length > 0 && showTrend && (
                        <Box sx={{ mt: 1 }}>
                            <Box sx={sectionCardSx}>
                                <TrendChart reportStyle trendData={trendData} showCost={showCost} showRevenue={showRevenue} noteType="stackadapt" />
                            </Box>
                        </Box>
                    )}

                    <Box sx={{ mt: 1 }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                            {(props.campaignData ?
                                <Box sx={{ flexGrow: 1, flexBasis: '100%', minWidth: 0 }}>
                                    <ReportTableCard branded={false}
                                        title="Campaigns"
                                        data={props.campaignData[channelKey].campaigns}
                                        count={(props.campaignData[channelKey].campaigns.length > 10 ? 10 : props.campaignData[channelKey].campaigns.length)}

                                        reportProfile={props.reportProfile}
                                        themeColor={{
                                            primary: theme.palette.primary.main,
                                            secondary: theme.palette.primary.main
                                        }}
                                        columns={tableColumns}
                                        renderDetailPanel={({ row }) => (
                                            <CreativeDetailTabs
                                                rowData={row.original}
                                                appVars={appVars}
                                                sDate={props.sDate}
                                                eDate={props.eDate}
                                                reportProfile={props.reportProfile}
                                                showRevenue={showRevenue}
                                            />
                                        )}
                                    />
                                </Box>
                                : <Box sx={sectionCardSx}><Typography variant='h6' align='center'>Loading Campaign Data</Typography><LinearProgress color='primary' sx={{ margin: theme.spacing(3) }}></LinearProgress></Box>
                            )}
                        </Box>
                    </Box>


                </Box>
            </>
            :
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
                <Box sx={{ ...surfaceCardSx, justifyContent: 'center', alignItems: 'center', p: theme.spacing(2.5), minWidth: 260 }}>
                    <Typography align="center" gutterBottom>No {props.title} Data</Typography>
                </Box>
            </Box>
        ))}

            <Dialog open={lightboxOpen} onClose={closeLightbox} maxWidth="md" fullWidth PaperProps={{ sx: surfaceCardSx }}>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: theme.palette.background.paper, borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.12)}` }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {lightboxTitle}
                    </Typography>
                    <IconButton onClick={closeLightbox} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    {lightboxSrc ? (
                        isVideoSrc(lightboxSrc) ? (
                            <Box
                                component="video"
                                src={lightboxSrc}
                                controls
                                autoPlay
                                sx={{ maxWidth: '100%', maxHeight: '70vh' }}
                            />
                        ) : (
                            <Box
                                component="img"
                                src={lightboxSrc}
                                alt={lightboxTitle}
                                sx={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
                                onError={(event) => {
                                    event.currentTarget.src = 'https://api.digitalwerks.com/imageFiles/no-image-icon.png';
                                }}
                            />
                        )
                    ) : null}
                </DialogContent>
            </Dialog>
        </>
    );
};

Video.propTypes = {

};

export default Video;
